import { Link, useNavigate } from 'react-router-dom';
import { Select, TextField } from '@nyce/admin-kit/ui';
import {
  EmptyState,
  ErrorBox,
  Loading,
  SortSelect,
  SortTh,
  TableScroller,
  useAsync,
  useSort,
} from '../../components/common';
import { formatCount } from '../../lib/format';
import {
  candidatePath,
  indexAdvisor,
  reviewError,
  reviewOf,
  type Candidate,
  type Scan,
} from '../api/indexAdvisor';
import {
  COVERAGE_OPTIONS,
  CoverageChip,
  formatShare,
  useParam,
  VERDICT_OPTIONS,
  VerdictChip,
} from '../shared';

const CONFIDENCE = { low: 0, medium: 1, high: 2 } as const;

/** The scan's candidates, most databases first: filter by table, coverage, verdict and count; a row opens its page. */
export function CandidatesTab({ scan, stateKey }: { scan: Scan; stateKey: string }) {
  const list = useAsync(() => indexAdvisor.candidates(scan.id), [scan.id, stateKey]);
  const [q, setQ] = useParam('q', '');
  const [coverage, setCoverage] = useParam('coverage', '');
  const [verdict, setVerdict] = useParam('verdict', '');
  const [min, setMin] = useParam('min', '');
  const navigate = useNavigate();
  const all = list.data?.candidates ?? [];
  const needle = q.trim().toLowerCase();
  const minDbs = Number(min) || 0;
  const filtered = all.filter((c) => {
    if (needle && !`${c.tableName} ${c.ddl}`.toLowerCase().includes(needle)) return false;
    if (coverage && c.coverage !== coverage) return false;
    if (minDbs && c.databases < minDbs) return false;
    if (verdict) {
      const r = reviewOf(c);
      if (verdict === 'unreviewed') return !r;
      if (r?.verdict !== verdict) return false;
    }
    return true;
  });
  const sort = useSort<Candidate>(
    filtered,
    {
      databases: { label: 'Databases', value: (c) => c.databases, desc: true },
      table: { label: 'Table', value: (c) => c.tableName },
      coverage: { label: 'Default set', value: (c) => c.coverage },
      verdict: { label: 'Review', value: (c) => reviewOf(c)?.verdict },
      confidence: {
        label: 'Confidence',
        value: (c) => {
          const r = reviewOf(c);
          return r ? CONFIDENCE[r.confidence] : null;
        },
        desc: true,
      },
    },
    useParam('sort', '')
  );
  const rows = sort.rows;
  const open = (c: Candidate) => navigate(candidatePath(scan.id, c.id));
  return (
    <>
      <div className="iax-toolbar">
        <TextField
          aria-label="Table"
          placeholder="Table or column"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Select
          aria-label="Default set"
          value={coverage}
          onChange={setCoverage}
          options={[{ value: '', label: 'Any coverage' }, ...COVERAGE_OPTIONS]}
        />
        <Select
          aria-label="Review"
          value={verdict}
          onChange={setVerdict}
          options={[
            { value: '', label: 'Any verdict' },
            { value: 'unreviewed', label: 'not reviewed' },
            ...VERDICT_OPTIONS,
          ]}
        />
        <TextField
          aria-label="Min databases"
          placeholder="Min databases"
          type="number"
          min={1}
          inputMode="numeric"
          fieldClassName="iax-num-field"
          value={min}
          onChange={(e) => setMin(e.target.value)}
        />
        <SortSelect sort={sort} />
        {list.data && (
          <span className="iax-muted">
            {formatCount(rows.length)} of {formatCount(all.length)}
          </span>
        )}
      </div>
      <ErrorBox error={list.error} />
      <Loading loading={list.loading && !list.data} />
      {list.data && !all.length && (
        <EmptyState
          card
          title={scan.status === 'succeeded' ? 'No candidates in this scan.' : 'No candidates yet.'}
          hint={
            scan.status === 'succeeded'
              ? 'No tenant database’s advisor recommended an index.'
              : 'They appear as the scan reads the databases.'
          }
        />
      )}
      {!!all.length && !rows.length && <EmptyState card title="No candidate matches the filters." />}
      {!!rows.length && (
        <div className="nyk-card">
          <TableScroller>
            <table
              className="nyk-ui-table nyk-cards"
              aria-label="Index candidates"
              data-testid="iax-candidates"
            >
              <thead>
                <tr>
                  <th>Index</th>
                  <SortTh sort={sort} by="table" />
                  <SortTh sort={sort} by="databases" className="nyk-ui-num" />
                  <SortTh sort={sort} by="coverage" />
                  <SortTh sort={sort} by="verdict" />
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const r = reviewOf(c);
                  const err = reviewError(c);
                  return (
                    <tr key={c.id} data-candidate={c.id} className="iax-clickable" onClick={() => open(c)}>
                      <td className="nyk-card-title">
                        <Link
                          to={candidatePath(scan.id, c.id)}
                          className="iax-ddl"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {c.ddl}
                        </Link>
                      </td>
                      <td data-label="Table" className="nyk-wrap">
                        {c.tableName}
                      </td>
                      <td data-label="Databases" className="nyk-ui-num">
                        {formatCount(c.databases)}
                        <div className="nyk-ui-hint">{formatShare(c.evidence.share)}</div>
                      </td>
                      <td data-label="Default set">
                        <CoverageChip coverage={c.coverage} coveredBy={c.coveredBy} />
                      </td>
                      <td data-label="Review">
                        {r ? (
                          <VerdictChip review={r} />
                        ) : err ? (
                          <span className="nyk-text-red nyk-wrap" title={err}>
                            review failed
                          </span>
                        ) : (
                          <span className="iax-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroller>
        </div>
      )}
    </>
  );
}
